// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Users, TrendingUp, ListTodo, Megaphone, Inbox as InboxIcon, LayoutDashboard, LifeBuoy, UserPlus,
  Mail, FileText, Handshake, Building2, Coins, Send, PackageCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// `roles` field: if set, tab is only shown to those roles. Undefined = all.
const TABS = [
  { to: "/app/crm", end: true, label: "Dashboard", icon: LayoutDashboard, tid: "crm-tab-dashboard", roles: ["admin","support","sales","marketing","b2b_admin"] },
  { to: "/app/crm/leads", label: "Leads", icon: Users, tid: "crm-tab-leads", roles: ["admin","support","sales","marketing","b2b_admin"] },
  { to: "/app/crm/deals", label: "Deals", icon: TrendingUp, tid: "crm-tab-deals", roles: ["admin","sales","marketing","b2b_admin"] },
  { to: "/app/crm/proposals", label: "Proposals", icon: FileText, tid: "crm-tab-proposals", roles: ["admin","sales","marketing","b2b_admin"] },
  { to: "/app/crm/handoffs", label: "Handoffs", icon: Handshake, tid: "crm-tab-handoffs", roles: ["admin","sales","support","logistics","b2b_admin"] },
  { to: "/app/crm/nurture", label: "Nurture", icon: Mail, tid: "crm-tab-nurture", roles: ["admin","marketing","sales"] },
  { to: "/app/crm/orgs", label: "Orgs", icon: Building2, tid: "crm-tab-orgs", roles: ["admin","sales","b2b_admin"] },
  { to: "/app/crm/referrers", label: "Referrers", icon: Coins, tid: "crm-tab-referrers", roles: ["admin","sales","marketing"] },
  { to: "/app/crm/marketing", label: "Marketing", icon: Send, tid: "crm-tab-marketing", roles: ["admin","marketing","sales"] },
  { to: "/app/crm/mail-in-log", label: "Mail-In Log", icon: PackageCheck, tid: "crm-tab-mailin", roles: ["admin","support","logistics"] },
  { to: "/app/crm/escalation", label: "Escalation", icon: LifeBuoy, tid: "crm-tab-escalation", roles: ["admin","support","sales","logistics","b2b_admin"] },
  { to: "/app/crm/tickets", label: "Tickets", icon: LifeBuoy, tid: "crm-tab-tickets" },  // all roles
  { to: "/app/crm/tasks", label: "Tasks", icon: ListTodo, tid: "crm-tab-tasks", roles: ["admin","support","sales","marketing","logistics","b2b_admin"] },
  { to: "/app/crm/employees", label: "Employees", icon: UserPlus, tid: "crm-tab-employees", roles: ["admin","b2b_admin","sales","support","marketing"] },
  { to: "/app/crm/campaigns", label: "Campaigns", icon: Megaphone, tid: "crm-tab-campaigns", roles: ["admin","marketing","sales"] },
  { to: "/app/crm/inbox", label: "Team Inbox", icon: InboxIcon, tid: "crm-tab-inbox", roles: ["admin","support","sales","marketing","logistics","b2b_admin"] },
];

/** Shared CRM layout — provides tabbed nav and outlet for sub-routes. */
export default function CrmShell() {
  const { user } = useAuth();
  const role = user?.role;
  const visibleTabs = TABS.filter((t) => !t.roles || t.roles.includes(role));
  const [config, setConfig] = useState(null);
  useEffect(() => {
    api.get("/crm/config").then(r => setConfig(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-secondary">CRM</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight">Customer & Sales Ops</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Internal comms, leads, deals, tasks and marketing campaigns — all in one place.
        </p>
      </div>

      <div className="border-b border-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {visibleTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.tid}
              className={({ isActive }) =>
                `px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet context={{ config }} />
    </div>
  );
}
